import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   PhoneIcon,
   SearchMagnifyingGlassIcon,
} from '@forwardflow/icons'
import {
   Autocomplete,
   Button,
   ContentCard,
   Grid,
   Group,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { useJsApiLoader } from '@react-google-maps/api'
import { ENV } from '@forwardflow/helpers/configs'

function useDebounce<T>(value: T, delay: number): T {
   const [debouncedValue, setDebouncedValue] = useState<T>(value)

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => {
         clearTimeout(handler)
      }
   }, [value, delay])

   return debouncedValue
}

const libraries: ['places'] = ['places']

export const PhoneNumberConfigurationV2 = () => {
   const navigate = useNavigate()
   const {
      getNextStep,
      getPreviousStep,
      onboardingData,
      setIsOnBoardingCompleted,
      setCampaignConfiguration,
      setIsCampaignConfigured,
   } = useOnboardingStoreV2()

   const { isLoaded } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY,
      libraries,
   })

   const [citySearch, setCitySearch] = useState('')
   const [selectedCity, setSelectedCity] = useState('')
   const [areaCodeSearch, setAreaCodeSearch] = useState('')
   const [citySuggestions, setCitySuggestions] = useState<string[]>([])
   const debouncedCitySearch = useDebounce(citySearch, 300)

   useEffect(() => {
      if (!debouncedCitySearch || selectedCity) {
         if (!debouncedCitySearch) setCitySuggestions([])
         return
      }

      if (!isLoaded) return

      let cancelled = false

      const fetchSuggestions = async () => {
         try {
            const request = {
               input: debouncedCitySearch,
               includedPrimaryTypes: ['(cities)'],
               includedRegionCodes: ['us'],
            }
            const { suggestions } =
               await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                  request
               )
            if (cancelled) return
            setCitySuggestions(
               [...new Set(
                  suggestions
                     .map((s) => {
                        const full = s.placePrediction?.text?.toString() ?? ''
                        return full.split(',')[0].trim()
                     })
                     .filter(Boolean)
               )]
            )
         } catch {
            if (!cancelled) setCitySuggestions([])
         }
      }

      fetchSuggestions()
      return () => {
         cancelled = true
      }
   }, [debouncedCitySearch, selectedCity, isLoaded])

   const isSelectingRef = useRef(false)

   const handleCityChange = (value: string) => {
      setCitySearch(value)
      if (isSelectingRef.current) {
         isSelectingRef.current = false
         return
      }
      setSelectedCity('')
   }

   const handleCitySelect = (value: string) => {
      isSelectingRef.current = true
      setCitySearch(value)
      setSelectedCity(value)
      setCitySuggestions([])
   }

   const { data: areaCodesResponse, isLoading } =
      useSearchAvailableAreaCodes(selectedCity)

   const normalizedAreaCodeSearch = areaCodeSearch.replace(/\D/g, '').slice(0, 3)
   const { data: searchedAreaCodesResponse, isLoading: isAreaCodeLoading } =
      useSearchAvailableAreaCodes(
         normalizedAreaCodeSearch.length === 3
            ? normalizedAreaCodeSearch
            : ''
      )

   const areaCodes = useMemo(() => {
      return onboardingData?.campaignConfiguration?.area_codes || []
   }, [onboardingData?.campaignConfiguration?.area_codes])

   const selectedAreaCodes = useMemo(() => {
      return areaCodes
   }, [areaCodes])

   const handleSelectAreaCode = (areaCode: string) => {
      if (selectedAreaCodes.length >= 3) return
      if (selectedAreaCodes.includes(areaCode)) return
      setCampaignConfiguration({
         area_codes: [...selectedAreaCodes, areaCode],
      })
   }

   const handleRemoveAreaCode = (areaCode: string) => {
      setCampaignConfiguration({
         area_codes: selectedAreaCodes.filter((code) => code !== areaCode),
      })
   }

   const onCompleteOnboarding = () => {
      if (isOperatorEmail(onboardingData?.email || '')) {
         navigate('/dashboard')
         return
      }
      setIsOnBoardingCompleted(true)
   }

   const handleNextStep = () => {
      onCompleteOnboarding()
      setIsCampaignConfigured(true)
      getNextStep()
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-6">
            <p className="text-white font-semibold text-lg">
               Select Phone Numbers
            </p>
            <Group className="rounded-[16px] border border-[#A684FF]/30 bg-[#A684FF]/5 px-6 py-3">
               <Group
                  justify="center"
                  align="center"
                  className="rounded-[8px] border-[0.667px] border-[#8E51FF]/30 w-10 h-10"
                  style={{
                     background:
                        'var(--fill-icon-linear, linear-gradient(135deg, rgba(79, 57, 246, 0.30) 0%, rgba(127, 34, 254, 0.30) 100%))',
                  }}
               >
                  <PhoneIcon color="#8E51FF" />
               </Group>
               <p className="text-white font-medium">
                  First 3 phone numbers to purchase is on us.
               </p>
            </Group>

            {selectedAreaCodes.length > 0 && (
               <div className="flex flex-wrap gap-2">
                  {selectedAreaCodes.map((code) => (
                     <div
                        key={code}
                        className="flex items-center gap-2 rounded-lg border border-[#9F7BFF]/40 bg-[rgba(159,123,255,0.20)] px-3 py-1.5 text-sm text-white"
                     >
                        <span>{code}</span>
                        <button
                           type="button"
                           onClick={() => handleRemoveAreaCode(code)}
                           className="text-white/60 hover:text-white transition-colors"
                        >
                           <svg
                              width={14}
                              height={14}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           >
                              <path d="M18 6L6 18M6 6l12 12" />
                           </svg>
                        </button>
                     </div>
                  ))}
               </div>
            )}

            <Autocomplete
               label="Search by city name"
               leftSection={<SearchMagnifyingGlassIcon width={16} />}
               shouldLeftSectionBeFocused
               placeholder="Search for a city to find available area codes..."
               value={citySearch}
               onChange={handleCityChange}
               onOptionSubmit={handleCitySelect}
               data={citySuggestions}
               filter={({ options }) => options}
            />

            <TextInput
               label="Or search directly by area code"
               leftSection={<SearchMagnifyingGlassIcon width={16} />}
               placeholder="Enter a 3-digit area code, e.g. 616"
               value={areaCodeSearch}
               onChange={(event) => {
                  setAreaCodeSearch(event.currentTarget.value.replace(/\D/g, '').slice(0, 3))
               }}
               inputMode="numeric"
               maxLength={3}
            />

            {normalizedAreaCodeSearch.length === 3 && (
               <div className="space-y-3">
                  <p className="text-white/60 text-sm">
                     {isAreaCodeLoading
                        ? 'Checking area code availability...'
                        : 'Area code results'}
                  </p>
                  {!isAreaCodeLoading &&
                     searchedAreaCodesResponse?.areaCodes?.map((code: number) => {
                        const areaCode = code.toString()
                        const isSelected = selectedAreaCodes.includes(areaCode)
                        return (
                           <button
                              key={areaCode}
                              type="button"
                              disabled={!isSelected && selectedAreaCodes.length >= 3}
                              onClick={() =>
                                 isSelected
                                    ? handleRemoveAreaCode(areaCode)
                                    : handleSelectAreaCode(areaCode)
                              }
                              className={`rounded-lg border px-4 py-2 text-sm transition-all ${
                                 isSelected
                                    ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                                    : 'border-white/20 bg-white/5 text-white hover:border-[#9F7BFF]/50 hover:bg-[rgba(159,123,255,0.10)]'
                              }`}
                           >
                              {areaCode}
                           </button>
                        )
                     })}
                  {!isAreaCodeLoading &&
                     searchedAreaCodesResponse?.areaCodes?.length === 0 && (
                        <p className="text-white/40 text-sm">
                           No available phone numbers found for area code {normalizedAreaCodeSearch}.
                        </p>
                     )}
               </div>
            )}

            <div>
               {isLoading && selectedCity && (
                  <div className="flex flex-wrap gap-2.5">
                     {[...Array(4)].map((_, i) => (
                        <div
                           key={i}
                           className="rounded-lg bg-white/10 animate-pulse h-10 w-28"
                        />
                     ))}
                  </div>
               )}

               {!isLoading && selectedCity && areaCodesResponse?.areaCodes && (
                  <div className="space-y-3">
                     <p className="text-white/60 text-sm">
                        Available area codes
                        {selectedAreaCodes.length >= 3 && (
                           <span className="text-[#A684FF] ml-2">
                              (Maximum 3 selected)
                           </span>
                        )}
                     </p>
                     <div className="flex flex-wrap gap-2.5">
                        {areaCodesResponse.areaCodes.map((code: number) => {
                           const areaCode = code.toString()
                           const isSelected =
                              selectedAreaCodes.includes(areaCode)
                           return (
                              <button
                                 key={areaCode}
                                 type="button"
                                 disabled={
                                    !isSelected &&
                                    selectedAreaCodes.length >= 3
                                 }
                                 onClick={() =>
                                    isSelected
                                       ? handleRemoveAreaCode(areaCode)
                                       : handleSelectAreaCode(areaCode)
                                 }
                                 className={`rounded-lg border px-4 py-2 text-sm transition-all ${
                                    isSelected
                                       ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                                       : selectedAreaCodes.length >= 3
                                         ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                                         : 'border-white/20 bg-white/5 text-white hover:border-[#9F7BFF]/50 hover:bg-[rgba(159,123,255,0.10)]'
                                 }`}
                              >
                                 {areaCode}
                              </button>
                           )
                        })}
                     </div>
                  </div>
               )}

               {!isLoading &&
                  selectedCity &&
                  areaCodesResponse?.areaCodes?.length === 0 && (
                     <p className="text-white/40 text-sm">
                        No area codes found for "{selectedCity}"
                     </p>
                  )}

               {!selectedCity && selectedAreaCodes.length === 0 && (
                  <p className="text-white/40 text-sm">
                     Start typing a city name to find available area codes
                  </p>
               )}
            </div>
         </ContentCard>
         <Grid>
            <Grid.Col span={3}>
               <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => getPreviousStep()}
               >
                  <ArrowLeftMdIcon width={12} /> Back
               </Button>
            </Grid.Col>
            <Grid.Col span={9}>
               <Button className="w-full" onClick={() => handleNextStep()}>
                  Save Campaign <ChevronRightIcon width={18} />
               </Button>
            </Grid.Col>
         </Grid>
      </Stack>
   )
}
